import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import useAuth from '../../hooks/useAuth'
import { db, isAbortError } from '../../lib/supabase'
import { scoreDCI, scoreIPIP } from '../../lib/scoringEngine'
import { DCI_SUBSCALES, IPIP_DOMAINS, DOMAIN_FACETS, IPIP_FACETS } from '../../lib/scoringConfig'
import categoryKeyMap from '../../lib/categoryKeyMap'

export default function ClientResults() {
  const { t } = useTranslation(['report', 'scoring'])
  const { user } = useAuth()
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [scores, setScores] = useState(null)

  useEffect(() => {
    async function loadResults() {
      try {
        const respondent = await db.getMyRespondent(user.id)
        if (!respondent?.completed_at) {
          setError(t('report:clientResults.surveyNotCompleted'))
          setLoading(false)
          return
        }

        const submission = await db.getSubmissionById(respondent.id)

        // Build lookups
        const likertMap = {}
        submission.likertResponses.forEach(r => { likertMap[r.question_id] = r.score })
        const profileMap = {}
        submission.profileResponses.forEach(r => { profileMap[r.question_id] = r.answer })

        const genderMap = { Male: 'M', Female: 'F' }
        const gender = genderMap[profileMap.q3]
        const age = parseInt(profileMap.q4)

        const dci = scoreDCI(likertMap)
        const ipip = scoreIPIP(likertMap, gender, age)

        setScores({ dci, ipip, name: respondent.name })
        setLoading(false)
      } catch (err) {
        if (isAbortError(err)) return
        console.error('Error loading results:', err)
        setError(err.message)
        setLoading(false)
      }
    }

    loadResults()
  }, [user.id, t])

  if (loading) {
    return <div className="text-center py-12 text-gray-500">{t('report:clientResults.loadingResults')}</div>
  }

  if (error) {
    return (
      <div className="max-w-xl mx-auto py-12 text-center">
        <div className="bg-red-50 border border-red-200 text-red-700 px-6 py-4 rounded-xl mb-4">
          {error}
        </div>
        <Link to="/client" className="text-primary-600 font-semibold hover:underline">
          {t('report:clientResults.backToDashboard')}
        </Link>
      </div>
    )
  }

  if (!scores) return null

  const { dci, ipip } = scores
  const tc = (cat) => {
    const key = categoryKeyMap[cat]
    return key ? t(`scoring:${key}`) : cat
  }

  return (
    <div className="max-w-3xl mx-auto animate-fadeIn">
      <div className="mb-6">
        <Link to="/client" className="text-gray-500 hover:text-gray-700 font-semibold transition-colors">
          {t('report:clientResults.backToDashboard')}
        </Link>
      </div>

      <h1 className="font-display text-2xl font-medium text-gray-800 mb-6">
        {t('report:clientResults.title')}
      </h1>

      {/* DCI Summary */}
      <div className="bg-white rounded-xl p-6 shadow-sm mb-6">
        <h2 className="font-display text-lg font-medium text-gray-800 mb-4">
          {t('report:clientResults.dciTitle')}
        </h2>
        <div className="mb-4 p-4 bg-primary-50 rounded-lg">
          <div className="text-sm font-semibold text-primary-600 mb-1">{t('report:clientResults.totalDciScore')}</div>
          <div className="text-2xl font-bold text-primary-700">{dci.total}</div>
          <div className="text-sm text-primary-600">{tc(dci.category)}</div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {Object.entries(DCI_SUBSCALES).map(([key]) => (
            <div key={key} className="p-3 bg-gray-50 rounded-lg">
              <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">{t(`scoring:dciSubscales.${key}`)}</div>
              <div className="text-lg font-bold text-gray-800">{dci.subscales[key]?.total ?? '\u2014'}</div>
            </div>
          ))}
        </div>
      </div>

      {/* IPIP Summary */}
      <div className="bg-white rounded-xl p-6 shadow-sm mb-6">
        <h2 className="font-display text-lg font-medium text-gray-800 mb-4">
          {t('report:clientResults.personalityProfile')}
        </h2>
        {Object.entries(IPIP_DOMAINS).map(([domainKey]) => {
          const domainData = ipip.domains[domainKey]
          if (!domainData) return null
          const facets = DOMAIN_FACETS[domainKey] || []
          return (
            <div key={domainKey} className="mb-6 last:mb-0">
              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg mb-2">
                <div>
                  <div className="font-semibold text-gray-800">{t(`scoring:ipipDomains.${domainKey}`)}</div>
                  <div className="text-xs text-gray-500">{t('report:clientResults.tScore')}</div>
                </div>
                <div className="text-right">
                  <div className="text-xl font-bold text-gray-800">{Math.round(domainData.tScore)}</div>
                  <div className="text-xs text-gray-500">{tc(domainData.categoryLabel)}</div>
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 pl-3">
                {facets.map(facetKey => {
                  const facetData = ipip.facets[facetKey]
                  if (!facetData) return null
                  return (
                    <div key={facetKey} className="flex items-center justify-between p-2 text-sm">
                      <span className="text-gray-600">{t(`scoring:ipipFacets.${facetKey}`)}</span>
                      <span className="font-semibold text-gray-800">
                        {Math.round(facetData.tScore)}
                        <span className="text-xs text-gray-400 ml-1">({tc(facetData.categoryLabel)})</span>
                      </span>
                    </div>
                  )
                })}
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}
